import { ICommonSettings, ITwinSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter, ConfigModalNote } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigTwinSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: ITwinSetting;
}

export interface IConfigTwinSettingsProps {
  formInputName: string[];
  closeModal: () => void;
  showModal: boolean;
  editingTwinId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigTwinSettings extends React.PureComponent<IConfigTwinSettingsProps, IConfigTwinSettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigTwinSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount(): void {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigTwinSettingsProps & IWithSDProps): void {
    if (prevProps.editingTwinId !== this.props.editingTwinId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = (): void => {
    if (!this.props.editingTwinId) return;

    const editingConfig = this.props.commonSettings.twinSettings?.find((x) => x.id === this.props.editingTwinId);

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    const twinSettings = this.props.commonSettings?.twinSettings;
    if (!twinSettings) return;

    const isSettingAlreadyExist = twinSettings.some((x) => x.name === value && x.id !== this.props.editingTwinId);

    if (isSettingAlreadyExist) throw new Error("This name already exists.");
  };

  onFinish = (data: ITwinSetting): void => {
    this.saveStatus.start();
    const obj: ITwinSetting = {
      id: this.props.editingTwinId || Guid.create().toString(),
      name: data.name,
      url: data.url?.trim(),
      embedToken: data.embedToken?.trim(),
      chatEnabled: data.chatEnabled ?? false,
      identitySecret: data.identitySecret?.trim()
    };

    if (this.props.editingTwinId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "twinSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "twinSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (): void => {};

  deleteSetting = (): void => {
    if (!this.props.editingTwinId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingTwinId || "",
      configKey: "twinSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`KuikAgents Integration setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render(): React.ReactNode {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="KuikAgents Integration"
        secondHeader="KuikAgents Integration"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingTwinId ? this.deleteSetting : undefined}
            deleteTestId="twinSettingDeleteButton"
            submitTestId="twinSettingCreateButton"
            submitLabel={this.props.editingTwinId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configTwinForm"
          />
        }
      >
        <Form
          id="configTwinForm"
          name="twinSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody>
            <ConfigModalNote>
              Connect a KuikAgents Integration. Enter the KuikAgents URL (the deployment origin — it serves both the
              dashboard / chat bundles and the API) and the access secret (an embed token created in KuikAgents →
              Settings → Embed, scoped to this project&apos;s origin).
            </ConfigModalNote>
            <ConfigField label="Name" required>
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="twinSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="KuikAgents URL" required>
              <Form.Item
                name="url"
                initialValue="https://agents.kuika.com"
                rules={[{ required: true, message: "Please enter the KuikAgents URL." }]}
              >
                <KuikaInput size="md" data-testid="twinSettingUrlInput" placeholder="https://agents.kuika.com" />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Access Secret (Embed Token)" required>
              <Form.Item name="embedToken" rules={[{ required: true, message: "Please enter the access secret." }]}>
                <KuikaInput
                  size="md"
                  type="password"
                  data-testid="twinSettingEmbedTokenInput"
                  placeholder="kE_…"
                  autoComplete="new-password"
                />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Identity Secret (per-user)" required>
              <Form.Item
                name="identitySecret"
                rules={[{ required: true, message: "Please enter the identity secret." }]}
              >
                <KuikaInput
                  size="md"
                  type="password"
                  data-testid="twinSettingIdentitySecretInput"
                  placeholder="kS_… — server-side only, links viewers as real KuikAgents users"
                  autoComplete="new-password"
                />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Show chat on all screens">
              <Form.Item name="chatEnabled" valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
                <KuikaSwitch dataTestId="twinSettingChatEnabledSwitch" />
              </Form.Item>
            </ConfigField>
            <ConfigModalNote>
              On: the agent chat button (bottom-right) appears on every screen except auth screens. Off: add the Agent
              Chat element to a screen to show chat only there.
            </ConfigModalNote>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
